import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import robinArt from "./robin.png";
import leftArt from "./left.png";

/* ---------------------------------------------------------------- *
 *  content
 * ---------------------------------------------------------------- */

const TICKER = [
  "the cave has no clock",
  "brain empty, life full",
  "he went in for five minutes and stayed for three days",
  "steal your own hours, spend them in the cave",
  "locked in is a place, not a mood",
  "no bosses, no noise, one screen",
];

const NAV = ["the legend", "gear", "notes", "the code", "colophon"];

const STORY = [
  "Long ago, in a forest called Sherwood, there was a man named Robin. He was fast. He was clever. And he could not sit still.",
  "One day Robin found a cave. Inside there were no clocks, no bosses, no noise. Just one screen and a lot of time.",
  "He went in for five minutes. He came out three days later. His brain was empty. He had never felt better.",
  "The rich men of the town worked all day and slept badly. Robin took the thing they wanted most — their hours — and he did not give them back. He kept them. He spent them all in the cave.",
  "They called him a thief. He called it a lifestyle. The people called him Robin Goon: the man who steals from the busy and gives to the locked-in.",
  "There was only one rule. Nobody opened the door. The cave was his, the hours were his, and no one ever knocked.",
  "Then one evening someone came looking for him and pushed the door open. The light fell in. And there he was — brain empty, screen glowing, three days deep. Caught.",
  "By morning the whole town knew. They laughed in the market. They laughed in the tavern. Robin Goon, the man who stole time, had been caught spending it.",
  "He could not take it. The shame was heavier than any clock. He packed nothing, said nothing, and walked into the trees. No one ever saw him again.",
  "Some say he found a deeper cave, one with a door that locks from the inside. Some say he is still out there, still locked in. Either way, nobody knocks anymore.",
];

const BELIEFS = [
  "the cave has no clock.",
  "empty brain is a good thing.",
  "sitting still is a skill.",
  "i watch, i don't rush.",
  "i share ideas, not orders.",
];

const DEN = [
  {
    title: "the cave",
    body: "a quiet, dark room. no windows. one screen. you go in, you lock in, and you lose track of time.",
    story:
      "The cave was not built. It was found. Robin walked past it a hundred times before the day he finally went in. Inside there was no morning and no evening — only the screen and the soft hum of a man losing track of himself. He liked it there. He liked it too much.",
  },
  {
    title: "goonOS",
    body: "a computer that shows one thing at a time. no clock. no pop-ups. it hides everything else so you can't look away.",
    story:
      "Robin wrote goonOS on a stolen laptop by candlelight. It had one window and one rule: show me the thing, hide the rest of the world. The townsfolk called it broken because it could not do their taxes. Robin called it finished.",
  },
  {
    title: "sessions",
    body: "an app that counts how long you stay locked in. it keeps your streak. it never tells you to stop.",
    story:
      "Every night Robin scratched a line on the cave wall. One line for each session. The lines became a wall of their own. He never counted them, he only added to them. A streak, he said, is a promise you keep to no one but yourself.",
  },
  {
    title: "tunnel vision",
    body: "glasses that blur everything except the middle of the screen. the edges go dark. only the goal is left.",
    story:
      "Robin ground two pieces of dark glass and tied them to his face. Through them the forest went black and only the middle stayed bright. His friends waved at the edges. He never saw them again — not because they left, but because he stopped looking.",
  },
  {
    title: "the vault",
    body: "one place for all your tabs and feeds. you pick what comes out. mostly, nothing does.",
    story:
      "Robin kept everything in the vault: his tabs, his feeds, his plans, his willpower. The rule was simple. Things go in. Things rarely come out. When people asked what he was saving it all for, he said 'later.' Later never came.",
  },
  {
    title: "colophon",
    body: "who wrote this, from which cave, and why it is a joke and not advice.",
    story:
      "This was written from a cave, by a man who is probably still in one. None of it is advice and none of it is a plan. It is a joke told by someone who forgot to leave. If you are reading this at 4am — close the laptop. Robin never did.",
  },
];

const MUSINGS = [
  {
    glyph: "▤",
    tone: "ink",
    title: "Empty brain, good day",
    date: "Jul 9, 2026",
    line: "when the noise stops, the day feels bigger.",
  },
  {
    glyph: "▒",
    tone: "ink",
    title: "The cave eats time",
    date: "Jul 6, 2026",
    line: "you go in at noon and come out in a new season.",
  },
  {
    glyph: "▮",
    tone: "red",
    title: "Time is the real price",
    date: "Jul 2, 2026",
    line: "you don't pay money to lock in. you pay hours.",
  },
];

/* ---------------------------------------------------------------- *
 *  typewriter — types text out when scrolled into view
 * ---------------------------------------------------------------- */

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

function Typewriter({ text, speed = 14, as = "span", className, ...rest }) {
  const reduce = usePrefersReducedMotion();
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [n, setN] = useState(0);

  // start when the element scrolls into view
  useEffect(() => {
    if (reduce) {
      setStarted(true);
      setN(text.length);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, text]);

  // type one character at a time
  useEffect(() => {
    if (!started || reduce || n >= text.length) return;
    const id = setTimeout(() => setN((v) => v + 1), speed);
    return () => clearTimeout(id);
  }, [started, n, text, speed, reduce]);

  const Tag = as;
  const done = n >= text.length;
  return (
    <Tag ref={ref} className={className} {...rest}>
      {text.slice(0, n)}
      {started && !done && <span className="tw-cursor">▋</span>}
    </Tag>
  );
}

/* ---------------------------------------------------------------- *
 *  small pieces
 * ---------------------------------------------------------------- */

function Divider({ label }) {
  return (
    <div className="divider" role="separator" aria-label={label}>
      <span className="divider-mark">::</span>
      <span className="divider-rule" aria-hidden="true">
        {"—.".repeat(160)}
      </span>
      <Typewriter as="span" className="divider-label" text={label} speed={26} />
      <span className="divider-mark">::</span>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 *  page
 * ---------------------------------------------------------------- */

export default function App() {
  const [openCard, setOpenCard] = useState(null);
  const toggle = (title) => setOpenCard((cur) => (cur === title ? null : title));

  return (
    <div className="site">
      <div className="scanlines" aria-hidden="true" />
      <div className="drops drops-b" aria-hidden="true" />
      <div className="drops drops-a" aria-hidden="true" />

      {/* ── ticker ─────────────────────────────────────────── */}
      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[...TICKER, ...TICKER].map((phrase, i) => (
            <span className="ticker-item" key={i}>
              {phrase}
              <span className="ticker-sep">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── nav ────────────────────────────────────────────── */}
      <header className="nav">
        <a className="brand" href="#top">
          ROBIN&nbsp;GOON
        </a>

        <div className="search">
          <input
            type="text"
            placeholder="search the cave"
            aria-label="search the cave"
          />
        </div>

        <nav className="links" aria-label="sections">
          {NAV.map((item) => (
            <a key={item} href={`#${item.replace(/\s+/g, "-")}`}>
              {item}
            </a>
          ))}
        </nav>

        <a
          className="twitter-btn"
          href="https://x.com/robingoon"
          target="_blank"
          rel="noopener noreferrer"
        >
          TWITTER
        </a>
      </header>

      <main id="top">
        {/* ── hero ─────────────────────────────────────────── */}
        <section className="hero">
          <h1 className="logo glitch" data-text="ROBIN GOON">
            ROBIN GOON
          </h1>

          <Typewriter
            as="p"
            className="tagline"
            speed={12}
            text="i'm robin goon. i live in the cave — a dark room, one screen, no clocks. i steal my own hours and spend them all in here. this is what i've learned."
          />

          <p className="watching">current session · 06:14:22</p>
        </section>

        {/* ── the legend ───────────────────────────────────── */}
        <Divider label="THE LEGEND" />
        <div className="legend-wrap" id="the-legend">
          <aside className="legend-art left">
            <img src={leftArt} alt="Robin Goon, mirrored pixel-art archer" />
          </aside>
          <section className="story">
            {STORY.map((para, i) => (
              <Typewriter
                key={i}
                as="p"
                className={i === 0 ? "story-open" : ""}
                speed={10}
                text={para}
              />
            ))}
          </section>
          <aside className="legend-art">
            <img src={robinArt} alt="Robin Goon, a pixel-art archer with a bow" />
          </aside>
        </div>

        {/* ── the goon code ────────────────────────────────── */}
        <Divider label="THE GOON CODE" />
        <ol className="beliefs" id="the-code">
          {BELIEFS.map((b, i) => (
            <li key={i}>
              <span className="num">{String(i + 1).padStart(2, "0")}</span>
              <Typewriter as="span" className="belief-text" text={b} />
            </li>
          ))}
        </ol>

        {/* ── goon gear ────────────────────────────────────── */}
        <Divider label="GOON GEAR" />
        <div className="den" id="gear">
          {DEN.map((card) => {
            const open = openCard === card.title;
            return (
              <article
                className={`card ${open ? "open" : ""}`}
                key={card.title}
                id={card.title.replace(/\s+/g, "-")}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => toggle(card.title)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(card.title);
                  }
                }}
              >
                <h3>
                  <span className="caret">{open ? "▾" : "›"}</span>{" "}
                  <Typewriter as="span" text={card.title} speed={22} />
                </h3>
                <Typewriter as="p" text={card.body} />
                {open ? (
                  <Typewriter as="p" className="card-story" speed={10} text={card.story} />
                ) : (
                  <span className="card-hint">click to read the tale →</span>
                )}
              </article>
            );
          })}
        </div>

        {/* ── goon notes ───────────────────────────────────── */}
        <Divider label="GOON NOTES" />
        <ul className="musings" id="notes">
          {MUSINGS.map((m) => (
            <li key={m.title}>
              <div className="musing-head">
                <span className={`glyph glyph-${m.tone}`}>{m.glyph}</span>
                <a className="musing-title" href="#notes">
                  <Typewriter as="span" text={m.title} speed={22} />
                </a>
                <span className="musing-date">{m.date}</span>
              </div>
              <Typewriter as="p" className="musing-line" text={m.line} />
            </li>
          ))}
        </ul>

        <a className="read-rest" href="#notes">
          → read the rest
        </a>
      </main>

      {/* ── footer ───────────────────────────────────────────── */}
      <footer className="foot">
        <Typewriter
          as="span"
          className="foot-chain"
          speed={16}
          text="the cave · no windows · low light · one screen · time drifting across it"
        />
        <Typewriter
          as="span"
          className="foot-note"
          speed={16}
          text="the session remembers · no advice, only perspective"
        />
      </footer>
    </div>
  );
}